import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';



import { ActionFunctionArgs, LoaderFunctionArgs, unstable_parseMultipartFormData as parseMultipartFormData } from '@remix-run/node';
import { Link, useFetcher, useNavigate, useRevalidator, useSearchParams } from '@remix-run/react';
import { withZod } from '@remix-validated-form/with-zod';
import { useQuery } from '@tanstack/react-query';
import { s3UploadHandler } from '~/.server/services/s3';
import ConditionalWrapper from '~/components/conditional-wrapper';
import CustomErrorBoundary from '~/components/custom-error-boundary';
import Layout from '~/components/layout';
import { OptimizedProductCard } from '~/components/optimized-product-card';
import { ProductCard } from '~/components/product-card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '~/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import ProductImageCarousel from '~/components/ui/embla-carousel/product-image-carousel';
import { Input } from '~/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import Viewer from '~/components/ui/rich-text/viewer';
import { Select, SelectContent, SelectItem, SelectTrigger } from '~/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { useToast } from '~/components/ui/use-toast';
import { bids as bidsTable } from '~/db/schema';
import { useRevalidateOnLotClose } from '~/hooks/use-revalidate-on-lot-close';
import { getTimedLotStatus } from '~/lib/lot-status';
import { apiWrapper, formatCurrency, formatDateTime, formatFileSize, getCategoryName, getEffectiveBidIncrement, getFormattedDistanceToNow, handleZodError } from '~/lib/utils';
import { createUserProfile, getAddressByUserId } from '~/services/api/addresses';
import { createUpdateAuctionRegistration, getAuctionRegistrationByUserIdAndAuctionId } from '~/services/api/auction-registrations';
import { getAuctionBySlug, getAuctionDetailsBasic } from '~/services/api/auctions';
import { validateLuciaSession } from '~/services/api/auth';
import { processLiveBid, processOnlineBid } from '~/services/api/auto-bid';
import { getBidIncrementsByAuction } from '~/services/api/bid-increments';
import { getProductBySlug, getProductList, optimizedGetProductList } from '~/services/api/products';
import { createUsers, getUserById } from '~/services/api/users';
import { AUCTION_EVENTS, validateAuctionEvent } from '~/services/events/auction-events';
import { PRODUCT_EVENTS, validateProductEvent } from '~/services/events/product-events';
import { USER_EVENTS, validateUserEvent } from '~/services/events/user-events';
import { createUserProfileValidator } from '~/services/models/addresses';
import {
  CreateUpdateAuctionRegistrationInput,
  createUpdateAuctionRegistrationValidator,
} from '~/services/models/auction-registrations';
import { userCreateValidator } from '~/services/models/users';
import { AuctionPropertyType, AuctionRegistrationStatus, AuctionType } from '~/types';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import ky from 'ky';
import { ArrowLeft, ArrowRight, Check, Dot, Download, File, Home, LoaderCircle, Search, Truck } from 'lucide-react';
import Pusher from 'pusher-js';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';
import { validationError } from 'remix-validated-form';
import { SpinnerCircularFixed } from 'spinners-react';
import { parse } from 'superjson';

import AuctionRegistration from './auction-registration';

interface ActionResponse {
  status: 'success' | 'error';
  message?: string;
  data?: any;
  error?: {
    message: string;
  };
}

type ActionType = 'createUsers' | 'createUserProfile' | 'createUpdateAuctionRegistration';

interface BidIncrement {
  priceFrom: number;
  priceTo: number;
  increments: number;
}

interface BidIncrementDisplayProps {
  bidIncrements: BidIncrement[];
}

const validators = {
  createUsers: withZod(userCreateValidator),
  createUserProfile: withZod(createUserProfileValidator),
  createUpdateAuctionRegistration: withZod(createUpdateAuctionRegistrationValidator),
} satisfies Record<ActionType, any>;

const creators = {
  createUsers: createUsers,
  createUserProfile: createUserProfile,
  createUpdateAuctionRegistration: createUpdateAuctionRegistration,
} satisfies Record<ActionType, any>;

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const loaderStart = Date.now();
  const timings: Record<string, number> = {};
  const measure = async <T,>(label: string, promise: Promise<T>): Promise<T> => {
    const start = Date.now();
    try {
      return await promise;
    } finally {
      timings[label] = Date.now() - start;
    }
  };

  const slug = params.slug as string;
  const url = new URL(request.url);
  const lotNumberSearch = url.searchParams.get('lot') || null;

  const initialLoadStart = Date.now();
  const [{ session }, product] = await Promise.all([
    measure('session', validateLuciaSession(request)),
    measure('product', getProductBySlug(slug)),
  ]);
  timings['session+product'] = Date.now() - initialLoadStart;

  if (!product) {
    throw new Response('Product Not Found', { status: 404 });
  }
  if (!product.auction) {
    throw new Response('Not Found', { status: 404 });
  }

  const auctionId = product.auction.id;
  const userPromise = session ? measure('user', getUserById(session.userId)) : Promise.resolve(null);
  if (!session) {
    timings.user = 0;
  }
  const routeDataStart = Date.now();
  const [user, auction, bidIncrements, productList] = await Promise.all([
    userPromise,
    measure('auctionDetailsBasic', getAuctionDetailsBasic(product.auction.slug)),
    measure('bidIncrements', getBidIncrementsByAuction(auctionId)),
    measure(
      'otherLots',
      optimizedGetProductList({
        sortByLotNumber: true,
        auctionId,
        limit: 9,
        lotNumber: lotNumberSearch,
      }),
    ),
  ]);
  timings.routeData = Date.now() - routeDataStart;

  if (!auction?.data) {
    throw new Response('Not Found', { status: 404 });
  }

  const userExtrasStart = Date.now();
  const [userAddress, auctionRegistration] = await Promise.all([
    user ? measure('userAddress', getAddressByUserId(user.id)) : Promise.resolve(null),
    user ? measure('auctionRegistration', getAuctionRegistrationByUserIdAndAuctionId(user.id, auction.data.id)) : Promise.resolve(null),
  ]);
  timings.userExtras = Date.now() - userExtrasStart;
  if (!user) {
    timings.userAddress = 0;
    timings.auctionRegistration = 0;
  }

  const responseStart = Date.now();
  const response = typedjson({
    bidIncrements,
    session,
    product,
    productList,
    user,
    userAddress,
    auction: auction.data,
    auctionRegistration,
  });
  timings.response = Date.now() - responseStart;
  timings.total = Date.now() - loaderStart;

  console.info(
    `[product-detail timings] slug=${slug} session=${timings.session}ms product=${timings.product}ms session+product=${timings['session+product']}ms auctionDetailsBasic=${timings.auctionDetailsBasic}ms bidIncrements=${timings.bidIncrements}ms otherLots=${timings.otherLots}ms routeData=${timings.routeData}ms user=${timings.user}ms userAddress=${timings.userAddress}ms auctionRegistration=${timings.auctionRegistration}ms userExtras=${timings.userExtras}ms response=${timings.response}ms total=${timings.total}ms`,
  );

  return response;
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const contentType = request.headers.get('Content-Type');

  try {
    if (contentType && contentType?.includes('multipart/form-data')) {
      const multipartFormData = await parseMultipartFormData(request, s3UploadHandler);

      const document = multipartFormData.get('document');
      const paymentDocument = multipartFormData.get('paymentDocument');

      return typedjson({
        document,
        paymentDocument,
      });
    } else {
      const formData = await request.formData();
      const action = formData.get('action');

      switch (action) {
        case 'createUpdateAuctionRegistration': {
          const data: CreateUpdateAuctionRegistrationInput = {
            auctionId: formData.get('auctionId') as string,
            status: formData.get('status') as string,
            userId: formData.get('userId') as string,
            bidderId: formData.get('bidderId') as string,
            documents: JSON.parse(formData.get('documents') as string),
            paymentDocument: JSON.parse(formData.get('paymentDocument') as string),
            metadata: JSON.parse(formData.get('metadata') as string),
          };

          const validatedData = createUpdateAuctionRegistrationValidator.parse(data);
          const createResult = await createUpdateAuctionRegistration(validatedData);

          if (createResult.status === 'success') {
            return typedjson({ status: 'success' });
          } else {
            throw new Error('Failed to submit registration');
          }
        }
        case 'createUsers': {
          const result = await validators['createUsers'].validate(formData);

          if (result.error) {
            return validationError(result.error);
          }

          const createResult = await creators['createUsers'](result.data);

          const response = {
            status: 'success',
            newUserId: createResult.newUserId,
            firstName: result.data.firstName,
            lastName: result.data.lastName,
            email: result.data.email,
          };
          return typedjson(response, {
            headers: {
              'Set-Cookie': createResult.cookie.serialize(),
            },
          });
        }
        case 'createBid': {
          const bidUserId = (formData.get('userId') as string) || null;

          if (!bidUserId) {
            await processLiveBid({
              productId: formData.get('productId') as string,
              auctionId: formData.get('auctionId') as string,
              userId: null,
              amount: Number(formData.get('maxBid')) || 0,
            });
            return typedjson({ status: 'success', message: 'Bid placed!' });
          }

          const rootDomain = request.url.split('/').slice(0, 3).join('/');

          const { userWon } = await processOnlineBid(
            {
              productId: formData.get('productId') as string,
              auctionId: formData.get('auctionId') as string,
              userId: bidUserId,
              maxBid: Number(formData.get('maxBid')) || 0,
            },
            rootDomain,
          );

          return typedjson({
            status: userWon ? 'success' : 'outbid',
            message: userWon ? 'Bid successful!' : 'You have been outbid.',
          });
        }
        default: {
          break;
        }
      }
    }
  } catch (error) {
    console.error(error);
    if (error instanceof Error) {
      return typedjson({ error: { message: error.message } });
    }
  }
};

export function ErrorBoundary() {
  return <CustomErrorBoundary />;
}

function ProductDetail() {
  const data = useTypedLoaderData<typeof loader>();
  const { product, productList, auction, auctionRegistration, user, session, bidIncrements } = data;

  const auctionStarted = useMemo(() => new Date() >= new Date(auction.auctionStart), [auction]);
  const auctionEnded = useMemo(() => {
    if (auction.auctionType === AuctionType.TIME2LIVE) {
      // For TIME2LIVE, lot is ended when the auctioneer hammers it — cutOffTime is set to now and liveBiddingMode is false
      return !product.liveBiddingMode && new Date() >= new Date(product.cutOffTime);
    }
    return new Date() >= new Date(product.cutOffTime);
  }, [auction, product]);

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: highestCurrentBid,
    isFetched: highestCurrentBidFetched,
    refetch: refetchHighestCurrentBid,
  } = useQuery<typeof bidsTable.$inferSelect>({
    queryKey: ['highest-bid', product.id],
    queryFn: async () => apiWrapper<typeof bidsTable.$inferSelect>(`/api/get-highest-bid/${product.id}`),
    gcTime: 0,
    retry: (failureCount, error) => {
      // Don't retry if the server is overloaded
      if (error.message.includes('Server is currently overloaded')) {
        return false;
      }
      return failureCount < 3;
    },
  });
  const {
    data: userCurrentBid,
    isFetched: userCurrentBidFetched,
    refetch: refetchUserCurrentBid,
  } = useQuery<typeof bidsTable.$inferSelect>({
    queryKey: ['user-current-bid', product.id],
    queryFn: async () => apiWrapper<typeof bidsTable.$inferSelect>(`/api/get-user-current-bid/${product.id}`),
    gcTime: 0,
    retry: (failureCount, error) => {
      // Don't retry if the server is overloaded
      if (error.message.includes('Server is currently overloaded')) {
        return false;
      }
      return failureCount < 3;
    },
  });
  const {
    data: highestMaxBid,
    isFetched: highestMaxBidFetched,
    refetch: refetchHighestMaxBid,
  } = useQuery<typeof bidsTable.$inferSelect>({
    queryKey: ['current-max-bid', product.id],
    queryFn: async () => apiWrapper<typeof bidsTable.$inferSelect>(`/api/get-current-max-bid/${product.id}`),
    gcTime: 0,
    retry: (failureCount, error) => {
      // Don't retry if the server is overloaded
      if (error.message.includes('Server is currently overloaded')) {
        return false;
      }
      return failureCount < 3;
    },
  });
  const { data: bidCount, refetch: refetchBidCount } = useQuery<number>({
    queryKey: ['bid-count', product.id],
    queryFn: async () => apiWrapper<number>(`/api/get-bid-count/${product.id}`),
    gcTime: 0,
    retry: (failureCount, error) => {
      // Don't retry if the server is overloaded
      if (error.message.includes('Server is currently overloaded')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const refetchBiddingData = useCallback(() => {
    refetchHighestCurrentBid();
    refetchUserCurrentBid();
    refetchBidCount();
    refetchHighestMaxBid();
  }, [refetchHighestCurrentBid, refetchUserCurrentBid, refetchBidCount, refetchHighestMaxBid]);

  const formattedHighestBid = useMemo(
    () =>
      highestCurrentBid
        ? formatCurrency(highestCurrentBid.amount)
        : bidCount
          ? '-'
          : formatCurrency(product.minimumPrice),
    [highestCurrentBid, product, bidCount],
  );

  const userOutbid = useMemo(() => {
    if (!highestMaxBid || !user?.id) return;

    if (highestMaxBid?.userId !== user.id) {
      return true;
    }

    return false;
  }, [highestMaxBid, userCurrentBid, user]);

  const userLostAuction = useMemo(() => userOutbid && auctionEnded, [userOutbid, auctionEnded]);
  const userWonAuction = useMemo(
    () => auctionEnded && highestCurrentBid && highestCurrentBid.userId === user?.id,
    [auctionEnded, highestCurrentBid, user],
  );

  const currentBidIncrement = useMemo(() => {
    if (!bidIncrements?.length) return 0;
    if (!highestCurrentBid) return getEffectiveBidIncrement(bidIncrements, product.minimumPrice);

    const relevantIncrement =
      highestCurrentBid?.amount > (userCurrentBid?.maxBid ?? 0)
        ? getEffectiveBidIncrement(bidIncrements, highestCurrentBid.amount)
        : getEffectiveBidIncrement(bidIncrements, userCurrentBid?.maxBid ?? 0);

    return relevantIncrement ?? bidIncrements[bidIncrements.length - 1].increments;
  }, [bidIncrements, userCurrentBid, highestCurrentBid]);

  const [showMoreBidOptions, setShowMoreBidOptions] = useState(false);

  const bidOptions = useMemo(() => {
    if (!highestCurrentBidFetched && !userCurrentBidFetched) return [];

    const options = new Set<number>();

    const firstOption =
      userCurrentBid && userCurrentBid.maxBid > (highestCurrentBid?.amount ?? 0)
        ? userCurrentBid.maxBid + currentBidIncrement
        : highestCurrentBid
          ? highestCurrentBid.amount + currentBidIncrement
          : (product?.minimumPrice ?? 0);
    options.add(firstOption);

    let attempts = 0;
    const maxAttempts = 100; // Adjust this value as needed

    while (options.size < 20 && attempts < maxAttempts) {
      const currentOption = Array.from(options)[options.size - 1];
      let effectiveIncrement = getEffectiveBidIncrement(bidIncrements, currentOption);

      // If we've reached the end of defined bid increments, use the last increment
      if (effectiveIncrement === undefined) {
        effectiveIncrement = bidIncrements[bidIncrements.length - 1].increments;
      }

      if (effectiveIncrement <= 0) {
        // If the increment is zero or negative, break the loop to avoid infinite repetition
        break;
      }

      const nextOption = currentOption + effectiveIncrement;
      if (nextOption <= currentOption) {
        // If the next option isn't increasing, break the loop
        break;
      }

      options.add(nextOption);
      attempts++;
    }

    if (attempts >= maxAttempts) {
      console.warn('Maximum attempts reached while generating bid options');
    }

    return Array.from(options);
  }, [
    highestCurrentBid,
    currentBidIncrement,
    product.minimumPrice,
    bidIncrements,
    userCurrentBid,
    userCurrentBidFetched,
    highestCurrentBidFetched,
  ]);

  const limitedBidOptions = useMemo(() => {
    return showMoreBidOptions ? bidOptions : bidOptions.slice(0, 12);
  }, [bidOptions, showMoreBidOptions]);
  const nextBid = useMemo(() => {
    const nextAmount =
      (userCurrentBid?.maxBid ?? 0) > (highestCurrentBid?.amount ?? 0)
        ? (userCurrentBid?.maxBid ?? 0) + currentBidIncrement
        : highestCurrentBid
          ? (highestCurrentBid?.amount ?? 0) + currentBidIncrement
          : product.minimumPrice + currentBidIncrement;

    return nextAmount.toString();
  }, [bidOptions, userCurrentBid, currentBidIncrement]);

  const [timeRemaining, setTimeRemaining] = useState('');
  const [lotStatusNow, setLotStatusNow] = useState(() => new Date());
  const timedLotStatus = getTimedLotStatus(
    {
      auctionType: auction.auctionType,
      auctionStart: auction.auctionStart,
      cutOffTime: product.cutOffTime,
      auctionLastCutOff: product.auctionLastCutOff,
      bidCount,
      lotUnsold: product.lotUnsold,
    },
    lotStatusNow,
  );
  const [bidSelectOpen, setBidSelectOpen] = useState(false);
  const [selectedBid, setSelectedBid] = useState('');
  const [searchText, setSearchText] = useState('');
  const [openConfirmBid, setOpenConfirmBid] = useState(false);
  const [warningBanner, setWarningBanner] = useState<'fair' | 'final' | 'sold' | null>(null);
  const warningAudioRef = useRef<HTMLAudioElement | null>(null);
  const finalWarningAudioRef = useRef<HTMLAudioElement | null>(null);

  const fetcher = useFetcher<ActionResponse>();

  // Reset the selected bid option when the product changes or selection becomes invalid
  useEffect(() => {
    if (!selectedBid || !bidOptions.includes(Number(selectedBid))) {
      setSelectedBid(bidOptions[0]?.toString());
    }
  }, [product.id, bidOptions]);
  useEffect(() => {
    // only run this once when all data has loaded
    if (highestCurrentBidFetched && userCurrentBidFetched && !selectedBid) {
      setSelectedBid(bidOptions[0].toString());
    }
  }, [highestCurrentBidFetched, userCurrentBidFetched, bidOptions, selectedBid]);
  useEffect(() => {
    if (userCurrentBid?.maxBid === Number(selectedBid)) {
      const nextBid = bidOptions.find((bid) => bid > Number(selectedBid));

      setSelectedBid(nextBid?.toString() ?? '');
    }
  }, [userCurrentBid, bidOptions, selectedBid]);

  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { revalidate } = useRevalidator();

  // The Pusher bindings below revalidate on bidding activity, which keeps this
  // page far fresher than the lot lists. They still leave the cutoff itself
  // uncovered: a lot that goes quiet before closing stamps from whatever was
  // last received.
  useRevalidateOnLotClose([product.cutOffTime]);

  const currentProductId = product.id;

  const handleSelectChange = (value: string) => {
    if (value !== 'show-more') {
      setSelectedBid(value);
      setBidSelectOpen(false);
    } else {
      setBidSelectOpen(true);
      setShowMoreBidOptions(true);
    }
  };

  const handleSubmitBid = () => {
    const formData = new FormData();

    formData.append('maxBid', selectedBid);
    formData.append('auctionId', auction.id);
    formData.append('productId', product.id);
    formData.append('userId', user?.id || '');
    formData.append('type', 'online');
    formData.append('action', 'createBid');

    fetcher.submit(formData, {
      method: 'post',
      action: `/product-detail/${product.slug}`,
    });
  };

  useEffect(() => {
    const pusher = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
      cluster: 'ap1',
    });

    const auctionChannel = pusher.subscribe(`auction-${auction.id}`);
    auctionChannel.bind(AUCTION_EVENTS.NewBid, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.NewBid,
          data: payload,
        });

        if (eventData.productId === product.id) {
          if (audioRef.current) {
            audioRef.current.play();
          }
          setWarningBanner(null);
          refetchBiddingData();
          revalidate();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.LiveBiddingStarted, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.LiveBiddingStarted,
          data: payload,
        });

        if (eventData.productId === product.id) {
          revalidate();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.LotSold, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.LotSold,
          data: payload,
        });

        if (eventData.productId === product.id) {
          setWarningBanner('sold');
          revalidate();
          refetchBiddingData();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.ResetWarning, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.ResetWarning,
          data: payload,
        }) as { data: { auctionId: string; productId: string } };

        if (eventData.productId === product.id) {
          setWarningBanner(null);
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.OpenBidding, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.OpenBidding,
          data: payload,
        }) as { data: { auctionId: string; productId: string } };

        if (eventData.productId === product.id) {
          setWarningBanner(null);
          revalidate();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.FairWarning, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.FairWarning,
          data: payload,
        }) as { data: { auctionId: string; productId: string } };

        if (eventData.productId === product.id) {
          setWarningBanner('fair');
          warningAudioRef.current?.play();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.FinalWarning, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.FinalWarning,
          data: payload,
        }) as { data: { auctionId: string; productId: string } };

        if (eventData.productId === product.id) {
          setWarningBanner('final');
          finalWarningAudioRef.current?.play();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    auctionChannel.bind(AUCTION_EVENTS.EndAuction, (payload: unknown) => {
      try {
        const { data: eventData } = validateAuctionEvent({
          event: AUCTION_EVENTS.EndAuction,
          data: payload,
        }) as { data: { auctionId: string; productId: string } };

        if (eventData.productId === product.id) {
          setWarningBanner('sold');
          revalidate();
        }
      } catch (err) {
        handleZodError(err);
      }
    });

    const productChannel = pusher.subscribe(`product-${product.id}`);
    productChannel.bind(PRODUCT_EVENTS.LockBid, (payload: unknown) => {
      try {
        const { data: eventData } = validateProductEvent({
          event: PRODUCT_EVENTS.LockBid,
          data: payload,
        });

        if (eventData.productId === product.id) {
          revalidate();
        }
      } catch (err) {
        handleZodError(err);
      }
    });
    productChannel.bind(PRODUCT_EVENTS.UnlockBid, (payload: unknown) => {
      try {
        const { data: eventData } = validateProductEvent({
          event: PRODUCT_EVENTS.UnlockBid,
          data: payload,
        });

        if (eventData.productId === product.id) {
          revalidate();
        }
      } catch (err) {
        handleZodError(err);
      }
    });

    if (user) {
      const userChannel = pusher.subscribe(`user-${user.id}`);
      userChannel.bind(USER_EVENTS.AuctionRegistrationApproved, (payload: unknown) => {
        try {
          const { data: eventData } = validateUserEvent({
            event: USER_EVENTS.AuctionRegistrationApproved,
            data: payload,
          });

          if (eventData.auctionId === auction.id) {
            revalidate();
          }
        } catch (err) {
          handleZodError(err);
        }
      });
      userChannel.bind(USER_EVENTS.UpdatedMaxBid, (payload: unknown) => {
        try {
          const { data: eventData } = validateUserEvent({
            event: USER_EVENTS.UpdatedMaxBid,
            data: payload,
          });

          if (eventData.auctionId === auction.id) {
            refetchBiddingData();
          }
        } catch (err) {
          handleZodError(err);
        }
      });
      userChannel.bind(USER_EVENTS.RegisteredForAuction, (payload: unknown) => {
        try {
          const { data: eventData } = validateUserEvent({
            event: USER_EVENTS.RegisteredForAuction,
            data: payload,
          });

          if (eventData.auctionId === auction.id) {
            revalidate();
          }
        } catch (err) {
          handleZodError(err);
        }
      });
    }

    const pollingInterval = setInterval(() => {
      refetchBiddingData();
      revalidate();
    }, 15000);

    return () => {
      clearInterval(pollingInterval);
      pusher.unsubscribe(`auction-${auction.id}`);
      pusher.unsubscribe(`product-${product.id}`);
      pusher.unsubscribe(`user-${user?.id}`);
      pusher.disconnect();
    };
    // ONLY INITIALIZE PUSHER ONCE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (fetcher.data.error) {
        toast({
          title: 'Warning',
          description: fetcher.data.error.message || 'An error occurred',
        });
        setOpenConfirmBid(false);
      } else if (fetcher.data.status === 'success') {
        toast({
          title: 'Success',
          description: 'Bid submitted successfully!',
        });
        setOpenConfirmBid(false);
        revalidate();
        refetchBiddingData();
      } else if (fetcher.data.status === 'outbid') {
        toast({
          title: 'Outbid',
          description: 'Your bid was placed but you have been outbid. Increase your maximum bid to take the lead.',
        });
        setOpenConfirmBid(false);
        revalidate();
        refetchBiddingData();
      }
    }
  }, [fetcher, toast]);

  useEffect(() => {
    if (product) {
      const updateRemainingTime = () => {
        const now = new Date();
        const timeLeft = getFormattedDistanceToNow(new Date(product.cutOffTime));
        setTimeRemaining(timeLeft);
        setLotStatusNow(now);

        const statusEnd =
          auction.auctionType === AuctionType.TIMEONLINE && product.auctionLastCutOff
            ? new Date(product.auctionLastCutOff)
            : new Date(product.cutOffTime);
        if (now >= statusEnd) clearInterval(intervalRef.current as NodeJS.Timeout);
      };

      intervalRef.current = setInterval(updateRemainingTime, 1000);

      return () => clearInterval(intervalRef.current as NodeJS.Timeout);
    }
  }, [auction.auctionType, product]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
      searchParams.set('lot', searchText);
      setSearchParams(searchParams, {
        preventScrollReset: true,
      });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const userRegistrationAwaitingApproval = useMemo(
    () =>
      auctionRegistration &&
      auctionRegistration.userId === user?.id &&
      auctionRegistration.status === AuctionRegistrationStatus.PENDING &&
      auctionRegistration.auctionId === auction.id,
    [auctionRegistration, user, auction],
  );
  const userRegistrationRejected = useMemo(
    () =>
      auctionRegistration &&
      auctionRegistration.userId === user?.id &&
      auctionRegistration.status === AuctionRegistrationStatus.REJECTED &&
      auctionRegistration.auctionId === auction.id,
    [auctionRegistration, user, auction],
  );
  const userRegistrationApproved = useMemo(
    () =>
      auctionRegistration &&
      auctionRegistration.userId === user?.id &&
      auctionRegistration.status === AuctionRegistrationStatus.APPROVED &&
      auctionRegistration.auctionId === auction.id,
    [auctionRegistration, user, auction],
  );
  const userRegistered = useMemo(() => {
    if (!userRegistrationApproved) return false;

    const metadata = auctionRegistration?.metadata as any;
    const selectedProducts = metadata?.selectedProducts;

    if (!selectedProducts) {
      return true;
    } else {
      return (selectedProducts || []).includes(product.id);
    }
  }, [auctionRegistration, product, userRegistrationApproved]);

  const currentIndex = useMemo(
    () => auction.products.findIndex((prod) => prod.id === currentProductId),
    [auction, currentProductId],
  );

  const prevProduct = useMemo(
    () => (currentIndex > 0 ? auction.products[currentIndex - 1] : null),
    [auction, currentIndex],
  );

  const nextProduct = useMemo(
    () => (currentIndex < auction.products.length - 1 ? auction.products[currentIndex + 1] : null),
    [auction, currentIndex],
  );

  const handlePrevClick = () => {
    if (prevProduct?.slug) {
      navigate(`/product-detail/${prevProduct.slug}`, { replace: true });
    }
  };

  const handleNextClick = () => {
    if (nextProduct?.slug) {
      navigate(`/product-detail/${nextProduct.slug}`, { replace: true });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const getBackgroundColor = (index: number) => {
    switch (index) {
      case 0:
        return 'bg-yellow-300';
      case 1:
        return 'bg-gray-400 text-white';
      case 2:
        return 'bg-amber-700/70 text-white';
      default:
        return 'bg-gray-100';
    }
  };

  return (
    <Layout user={user as unknown as NonNullable<Awaited<ReturnType<typeof getUserById>>>}>
      <audio ref={audioRef} preload="auto">
        <source src="/images/sound/click-alert.mp3" type="audio/mpeg" />
        <track kind="captions" />
      </audio>
      <audio ref={warningAudioRef} preload="auto">
        <source src="/images/sound/fair-warning.mp3" type="audio/mpeg" />
        <track kind="captions" />
      </audio>
      <audio ref={finalWarningAudioRef} preload="auto">
        <source src="/images/sound/final-warning.mp3" type="audio/mpeg" />
        <track kind="captions" />
      </audio>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/auctions" className="text-sm font-normal">
              Auctions
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href={`/auction-detail/${auction.slug}`} className="text-sm font-normal">
              {auction.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-sm font-normal">{product.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <div className="relative w-full">
            <ProductImageCarousel images={product.images} options={{ loop: true, duration: 30 }} />
            {timedLotStatus ? (
              timedLotStatus.phase === 'closed' && timedLotStatus.outcome !== null ? (
                <img
                  src={`/images/${timedLotStatus.outcome}.png`}
                  alt={timedLotStatus.outcome}
                  className="absolute -left-8 -top-5 h-20 w-24"
                  loading="lazy"
                />
              ) : null
            ) : !auctionEnded && !product.lotUnsold ? null : !product.lotUnsold && (bidCount || 0) > 0 ? (
              <img src={'/images/sold.png'} alt="sold" className="absolute -left-8 -top-5 h-20 w-24" loading="lazy" />
            ) : (
              <img
                src={'/images/unsold.png'}
                alt="unsold"
                className="absolute -left-8 -top-5 h-20 w-24"
                loading="lazy"
              />
            )}
          </div>
          {/* Desktop */}
          <Accordion type="multiple" className="hidden w-full flex-col lg:flex">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-base font-semibold text-slate-900">Overview</AccordionTrigger>
              <AccordionContent>
                <Viewer content={product?.description || '-'} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-base font-semibold text-slate-900">Estimated Price</AccordionTrigger>
              <AccordionContent className="text-pretty text-base font-normal text-muted-foreground">
                {product.maximumPrice > 0
                  ? `${formatCurrency(product.minimumPrice ?? 0)} - ${formatCurrency(product.maximumPrice ?? 0)}`
                  : formatCurrency(product.minimumPrice ?? 0)}
              </AccordionContent>
            </AccordionItem>
            {auction.propertyType === AuctionPropertyType.NON_PROPERTY && (
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-base font-semibold text-slate-900">
                  Payment & Shipping
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-pretty text-base text-muted-foreground">
                  <div className="flex flex-col">
                    <p className="font-semibold">Payment</p>
                    <Viewer content={auction.paymentInformation} />
                  </div>
                  <div className="flex flex-col">
                    <p className="font-semibold">Shipping</p>
                    <Viewer content={auction.shippingInformation} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {auction.propertyType !== AuctionPropertyType.NON_PROPERTY && (
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-base font-semibold text-slate-900">Postal Address</AccordionTrigger>
                <AccordionContent className="text-pretty text-base font-normal text-muted-foreground">
                  {product?.property?.postalAddress || '-'}
                </AccordionContent>
              </AccordionItem>
            )}
            {auction.auctionDocuments.length > 0 && (
              <AccordionItem value="item-5">
                <AccordionTrigger className="text-base font-semibold text-slate-900">Documents</AccordionTrigger>
                <AccordionContent className="text-pretty text-base font-normal text-muted-foreground">
                  {auction.auctionDocuments.map((document, index) => (
                    <div className="my-2 flex items-center justify-between" key={index}>
                      <div className="flex items-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg border">
                          <File />
                        </div>
                        <div className="ml-4 flex flex-col">
                          <span className="text-sm">{document?.name}</span>
                          <span className="text-xs text-slate-500">{formatFileSize(document?.size)}</span>
                        </div>
                      </div>
                      <div>
                        <Button variant="ghost" className="p-2">
                          <a href={document.src} target="_blank" rel="noreferrer" download>
                            <Download size={16} />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>

        <div className="flex flex-col gap-4">
          <div className="-mb-2 flex flex-row items-center justify-between text-sm text-slate-500">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant={'ghost'} className="p-0 font-normal">
                  <Search className="mr-1.5 h-4 w-4" /> Search Lot
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px]">
                <div className="flex flex-col text-sm text-muted-foreground">
                  <div>
                    <p>Enter a search number to skip to a specific log number within this auction event</p>
                    <Input
                      placeholder="Search lot number"
                      value={searchText}
                      onChange={handleSearchChange}
                      className="my-2"
                    />
                  </div>
                  {searchText && productList.totalCount > 0 && (
                    <div>
                      {productList.list.map((product) => (
                        <div key={product.id}>
                          <Link
                            to={`/product-detail/${product.slug}`}
                            className="flex flex-row gap-2 hover:bg-slate-50"
                          >
                            <img
                              src={product.imageUrl ? product.imageUrl : '/images/placeholder-image-600x600.png'}
                              alt={product.name}
                              loading="lazy"
                              className="mb-2 h-20 w-20 object-cover"
                            />
                            <div>
                              <p className="font-semibold">Lot-{product.lotNumber}</p>
                              <p>{product.name}</p>
                              <p className="text-xs font-medium text-black">
                                {(bidCount || 0) > 0 ? formattedHighestBid : formatCurrency(product.minimumPrice)}{' '}
                                <span className="text-xs font-normal text-slate-500">({bidCount} bids)</span>
                              </p>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <div className="flex flex-row items-center gap-5">
              <Button variant={'ghost'} className="p-0 font-normal" onClick={handlePrevClick} disabled={!prevProduct}>
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Prev
              </Button>
              <Button variant={'ghost'} className="p-0 font-normal" onClick={handleNextClick} disabled={!nextProduct}>
                Next
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="hidden flex-row items-center gap-1 border-t border-slate-200 pt-4 text-xs font-normal text-slate-500 lg:flex">
            <p>{getCategoryName(product.productsToCategories?.[0]?.categoryId)}</p>
            <Dot className="h-3 w-3" />
            <p>{auction.auctionType === AuctionType.TIMEONLINE ? 'TimeOnline' : 'Time2Live'}</p>
            <Dot className="h-3 w-3" />
            <p>Start: {formatDateTime(auction.auctionStart)} GMT +8</p>
            <Dot className="h-3 w-3" />
            <p>Lot-{product.lotNumber}</p>
          </div>

          {/* Mobile */}
          <div className="flex flex-row items-center gap-1 border-t border-slate-200 pt-4 text-xs font-normal text-slate-500 lg:hidden">
            <p>{getCategoryName(product.productsToCategories?.[0]?.categoryId)}</p>
            <Dot className="h-3 w-3" />
            <p>{auction.auctionType === AuctionType.TIMEONLINE ? 'TimeOnline' : 'Time2Live'}</p>
            <Dot className="h-3 w-3" />
            <p>Lot-{product.lotNumber}</p>
          </div>
          <div className="flex flex-row items-center gap-1 text-xs font-normal text-slate-500 lg:hidden">
            <p>Start: {formatDateTime(auction.auctionStart)} GMT +8</p>
          </div>
          <div className="border-t border-slate-200"></div>

          <p className="text-xl font-medium">{product.name}</p>
          {warningBanner && (
            <div
              className={clsx(
                'mb-3 flex animate-pulse items-center justify-between rounded-md border px-4 py-3 text-sm font-semibold',
                {
                  'border-yellow-400 bg-yellow-50 text-yellow-800': warningBanner === 'fair',
                  'border-orange-500 bg-orange-50 text-orange-900': warningBanner === 'final',
                  'border-green-500 bg-green-50 text-green-900': warningBanner === 'sold',
                },
              )}
            >
              <span>
                {warningBanner === 'fair' && '⚠️ Fair Warning'}
                {warningBanner === 'final' && '🔔 Final Warning'}
                {warningBanner === 'sold' && '🔨 Sold! This lot has been hammered down.'}
              </span>
              <button className="ml-4 text-xs font-normal underline opacity-70" onClick={() => setWarningBanner(null)}>
                Dismiss
              </button>
            </div>
          )}
          <div className="flex flex-col border border-slate-200 bg-[#FCFCFC] p-4">
            {userCurrentBid && (
              <div className="mb-2 flex flex-col gap-2 border-b border-slate-200 pb-4">
                <p
                  className={clsx('text-sm font-medium', {
                    'text-red1': userLostAuction || userOutbid,
                    'text-green1': userWonAuction || !userLostAuction || !userOutbid,
                  })}
                >
                  {userLostAuction
                    ? 'You lost this lot.'
                    : userOutbid
                      ? 'You have been outbid!'
                      : userWonAuction
                        ? 'You won this lot!'
                        : 'You are the Leading Bidder!'}
                </p>
                <p className="text-xs font-normal text-slate-500">
                  {userLostAuction
                    ? 'You were outbid! Better luck next time!'
                    : userOutbid
                      ? 'Uh-oh! Your initial leading bid amount has been out bid by a competing bidder. Raise your bid to increase your chances of winning.'
                      : userWonAuction
                        ? 'Congratulations! You have successfully won this lot. An email on how to pay for this lot, and other you have won, will be sent to your email.'
                        : 'Congratulations! Your bid amount is currently in the lead. In the meantime, raise your bid to increase your chances of winning.'}
                </p>
              </div>
            )}
            <div className="flex flex-row justify-between text-xs font-normal">
              <p>
                {bidCount === 0
                  ? 'Starting Price'
                  : auctionEnded && (bidCount || 0) > 0
                    ? 'Sold Price'
                    : 'Current Price'}
              </p>
              <p className="text-slate-500">{bidCount} bids</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <p className="text-lg font-bold">{formattedHighestBid}</p>
            </div>
            <p className="text-xs font-normal text-slate-500">
              Estimated Price{' '}
              {product.maximumPrice > 0
                ? `${formatCurrency(product.minimumPrice ?? 0)} - ${formatCurrency(product.maximumPrice ?? 0)}`
                : formatCurrency(product.minimumPrice ?? 0)}
            </p>
            {userCurrentBid?.maxBid ? (
              <p className="text-sm text-orange-500">
                Your current maximum bid is: {formatCurrency(userCurrentBid.maxBid)}
              </p>
            ) : null}

            {auctionEnded || product.lotUnsold ? (
              <Button variant="outline" className="pointer-events-none my-4 hover:bg-white">
                {product.lotUnsold && !auctionEnded ? 'Lot Unavailable' : 'Auction Ended'}
              </Button>
            ) : userRegistered ? (
              <>
                {auction.auctionType !== AuctionType.LIVEONLY && auctionStarted ? (
                  <div className="-ml-1 flex flex-row items-center gap-2">
                    <Select onValueChange={handleSelectChange} open={bidSelectOpen} onOpenChange={setBidSelectOpen}>
                      <SelectTrigger className="col-span-1 mt-2" disabled={!!product.lockBidding}>
                        {selectedBid ? formatCurrency(Number(selectedBid)) + '.00' : 'Select Option'}
                      </SelectTrigger>
                      <SelectContent>
                        {limitedBidOptions.map((bid, index) => (
                          <SelectItem key={index} value={bid.toString()}>
                            {`${formatCurrency(bid)}.00`}
                          </SelectItem>
                        ))}
                        {!showMoreBidOptions && bidOptions.length > 15 && (
                          <SelectItem key="show-more" value="show-more">
                            Show more
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <ConditionalWrapper
                      condition={!!product.lockBidding}
                      wrapper={(children) => (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>{children}</TooltipTrigger>
                            <TooltipContent>
                              <p className="text-sm">Bidding is currently locked by the vendor</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    >
                      <Button
                        className="mt-2 w-1/4"
                        variant={'primary'}
                        onClick={() => {
                          // if the selected max bid is equal to the next sequential bid, no need "Are you sure?" popup
                          if (selectedBid === nextBid) {
                            handleSubmitBid();
                            return;
                          }

                          setOpenConfirmBid(true);
                        }}
                        disabled={fetcher.state === 'submitting' || !!product.lockBidding}
                      >
                        {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
                        Place Bid
                      </Button>
                    </ConditionalWrapper>
                  </div>
                ) : auction.auctionType === AuctionType.LIVEONLY && auctionStarted && product.liveBiddingMode ? (
                  <Link to={`/auction-live/${auction.slug}`}>
                    <Button className="mt-2 w-full" variant="primary">
                      Enter Live Auction
                    </Button>
                  </Link>
                ) : (
                  <div className="my-3 flex w-full flex-row items-center justify-center gap-2 rounded border border-green1 p-2 text-green1">
                    <Check color="#2B9A66" size={20} />
                    <p className="text-center text-sm font-semibold">Registration Approved</p>
                  </div>
                )}
                {!!product.lockBidding && (
                  <p className="my-1 text-xs text-red-500">Bidding is currently locked by the vendor</p>
                )}
              </>
            ) : (
              <>
                {userRegistrationAwaitingApproval ? (
                  <div className="my-3 flex w-full flex-row items-center justify-center gap-2 rounded border border-green1 p-2 text-green1">
                    <SpinnerCircularFixed color="#2B9A66" size={20} secondaryColor="#FFFFFF" />
                    <p className="text-center text-sm font-semibold">Awaiting Approval</p>
                  </div>
                ) : (session && !userRegistered) || !session ? (
                  <>
                    <AuctionRegistration
                      data={auction as unknown as Awaited<ReturnType<typeof getAuctionDetailsBasic>>['data']}
                      userId={user?.id}
                      isLoggedIn={session ? true : false}
                    />
                    {userRegistrationRejected && (
                      <p className="mb-2 mt-3 text-center text-sm text-slate-600">
                        *Your registration has been rejected. Please try again.
                      </p>
                    )}
                  </>
                ) : null}
              </>
            )}

            <p className="mt-2 text-xs font-normal text-slate-500">
              Lot Number: <span className="font-bold text-slate-900">Lot-{product.lotNumber}</span>
            </p>
            {auctionStarted && (
              <p className="my-1 text-xs font-normal text-slate-500">
                Current Bid Increment: <span className="font-bold text-slate-900">{currentBidIncrement}</span>
              </p>
            )}
            {auctionRegistration?.bidderId !== undefined && (
              <p className="mb-3 border-b border-slate-200 pb-3 text-xs font-normal text-slate-500">
                Your Bidder Number: <span className="font-bold text-slate-900">{auctionRegistration?.bidderId}</span>
              </p>
            )}
            {auction.liveAuctionDate && (
              <p className="mt-2 text-xs font-normal text-slate-500">
                Live auction starts on:{' '}
                <span className="font-semibold text-slate-900">{formatDateTime(auction.liveAuctionDate)} GMT +8</span>
              </p>
            )}
            {timedLotStatus ? (
              timedLotStatus.phase === 'scheduled' ? (
                <p className="mt-2 text-xs font-normal text-slate-500">
                  Auction starts:{' '}
                  <span className="text-red-600">
                    {formatDistanceToNow(auction.auctionStart, {
                      addSuffix: true,
                      includeSeconds: true,
                    })}
                  </span>
                </p>
              ) : timedLotStatus.phase === 'closed' ? (
                timedLotStatus.outcome === null ? (
                  <p className="mt-2 text-xs font-normal text-slate-500">Bidding closed</p>
                ) : timedLotStatus.endsAt !== null ? (
                  <p className="mt-2 text-xs font-normal text-slate-500">
                    Auction ended on: {formatDateTime(timedLotStatus.endsAt)}
                  </p>
                ) : null
              ) : (
                <p className="text-xs font-normal text-slate-500">
                  Auction ending in: <span className="text-red-600">{timeRemaining}</span>
                </p>
              )
            ) : !auctionStarted ? (
              <p className="mt-2 text-xs font-normal text-slate-500">
                Auction starts:{' '}
                <span className="text-red-600">
                  {formatDistanceToNow(auction.auctionStart, {
                    addSuffix: true,
                    includeSeconds: true,
                  })}
                </span>
              </p>
            ) : auctionEnded ? (
              <p className="mt-2 text-xs font-normal text-slate-500">
                Auction ended on: {formatDateTime(auction.auctionType === AuctionType.TIME2LIVE && auction.auctionEnd ? auction.auctionEnd : product.cutOffTime)}
              </p>
            ) : auction.auctionType === AuctionType.TIME2LIVE && product.liveBiddingMode ? (
              <p className="mt-2 text-xs font-normal text-red-600">
                ⚡ Live auction in progress — the hammer may fall at any time.
              </p>
            ) : auction.auctionType !== AuctionType.TIME2LIVE ? (
              <p className="text-xs font-normal text-slate-500">
                Auction ending in: <span className="text-red-600">{timeRemaining}</span>
              </p>
            ) : null}
            <Popover>
              <PopoverTrigger asChild>
                <p className="mt-2 w-full cursor-pointer text-xs text-slate-500 hover:underline">*Bids Increments Chart 🡐</p>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <BidIncrementDisplay bidIncrements={bidIncrements} />
              </PopoverContent>
            </Popover>
            {product.liveBiddingMode &&
              !auctionEnded &&
              userRegistered &&
              auction.auctionType === AuctionType.TIME2LIVE && (
                <>
                  <p className="mt-4 border-t border-slate-200 pt-2 text-xs text-red-500">Auction is Live. Bid now!</p>
                  <Button
                    variant={'outline'}
                    onClick={() => {
                      navigate(`/auction-live/${auction.slug}`);
                    }}
                    className="mt-2"
                  >
                    Enter Live Auction
                  </Button>
                </>
              )}
            {product.liveBiddingMode && !auctionEnded && (
              <Link to={`/auction-live/public/${auction.slug}`}>
                <Button className="mt-2 w-full" variant="default">
                  View Live Auction
                </Button>
              </Link>
            )}
          </div>
          {auction.propertyType !== AuctionPropertyType.NON_PROPERTY && (
            <>
              <div className="h-[1px] bg-slate-200" />
              <div className="flex w-full flex-col">
                <p className="mb-2 text-sm font-semibold">Specifications</p>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Type of Property</p>
                  <p className="text-sm font-medium capitalize">{product.property?.type.replace('_', ' ') || '-'}</p>
                </div>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Tenure</p>
                  <p className="text-sm font-medium capitalize">{product.property?.tenure.replace('_', ' ') || '-'}</p>
                </div>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Size</p>
                  <p className="text-sm font-medium capitalize">{product.property?.size.replace('_', ' ') || '-'}</p>
                </div>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Reference Number</p>
                  <p className="text-sm font-medium capitalize">
                    {product.property?.referenceNumber.replace('_', ' ') || '-'}
                  </p>
                </div>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Assignee Bank</p>
                  <p className="text-sm font-medium capitalize">{product.property?.assignee || '-'}</p>
                </div>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Assignor/Borrower(s)</p>
                  <p className="text-sm font-medium capitalize">{product.property?.borrower || '-'}</p>
                </div>
                <div className="mt-1 flex w-full items-center justify-between">
                  <p className="text-sm text-slate-500">Bank Solicitor</p>
                  <p className="text-sm font-medium capitalize">{product.property?.bankSolicitor || '-'}</p>
                </div>
              </div>
            </>
          )}
          {product.bids.length > 0 && (
            <>
              <div className="h-[1px] bg-slate-200" />
              <div className="flex flex-col gap-2">
                <div>
                  <p className="text-sm font-semibold">Event Results</p>
                  {product.bids
                    .sort((a, b) => b.amount - a.amount)
                    .slice(0, 5)
                    .map((bid, index) => {
                      const auctionRegistration = bid.user?.auctionRegistrations?.find(
                        (registration) =>
                          registration.auctionId === product.auctionId &&
                          registration.status === AuctionRegistrationStatus.APPROVED,
                      );
                      const bidderId = auctionRegistration ? auctionRegistration.bidderId : 'On-site bidder';

                      return (
                        <div key={index} className="mt-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <p
                              className={`border-r-1 flex h-5 w-5 items-center justify-center rounded-full text-xs ${getBackgroundColor(
                                index,
                              )}`}
                            >
                              {index + 1}
                            </p>
                            <p className="text-sm font-normal">{formatCurrency(bid.amount)}</p>
                            <p>&middot;</p>
                            <p className="text-xs text-slate-500">Online (ID: {bidderId})</p>
                          </div>
                          <p className="text-xs text-slate-500">{formatDateTime(bid.createdAt)}</p>
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}
          <div className="h-[1px] bg-slate-200" />
          <div className="flex flex-col gap-2">
            <div>
              <p className="text-sm font-semibold">Shipping & Pickup Options</p>
              <p className="text-xs font-normal text-slate-500">
                Item located in {auction.auctionHouse?.address?.city}, {auction.auctionHouse?.address?.state},{' '}
                {auction.auctionHouse?.address?.country}
              </p>
            </div>
            <div className="mt-2 flex flex-row items-center gap-1">
              <Truck className="mr-1 scale-x-[-1] transform" size={25} strokeWidth={1} />
              <p className="text-sm font-normal">Offers In-House Shipping</p>
            </div>
            <Button variant={'link'} size={'sm'} className="justify-start p-0 font-normal text-blue1">
              View Auction Policy for Details
            </Button>
          </div>
          <div className="h-[1px] bg-slate-200" />
          <div className="flex flex-col gap-2">
            <div>
              <p className="text-sm font-semibold">Payment</p>
              <p className="text-xs font-normal text-slate-500">
                Item located in {auction.auctionHouse?.address?.city}, {auction.auctionHouse?.address?.state},{' '}
                {auction.auctionHouse?.address?.country}
              </p>
            </div>
            <div className="mt-2 flex flex-row items-center gap-1">
              <Truck className="mr-1 scale-x-[-1] transform" size={25} strokeWidth={1} />
              <p className="text-sm font-normal">Accepts seamless payments through Best2bid</p>
            </div>
            <Button variant={'link'} size={'sm'} className="justify-start p-0 font-normal text-blue1">
              View Auction Policy for Details
            </Button>
          </div>
          <div className="h-[1px] bg-slate-200" />
          <div className="flex flex-col gap-4">
            <p className="-mb-2 text-sm font-semibold">About Auction</p>
            <Link to={`/auction-house/${auction.auctionHouse?.slug}`}>
              <div className="flex flex-row items-center gap-2 hover:underline">
                <Avatar className="h-8 w-8 border">
                  <AvatarImage src={auction.auctionHouse.image} alt={auction.auctionHouse.name} />
                  <AvatarFallback className="bg-black text-white">
                    {auction.auctionHouse?.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <p className="text-sm font-semibold">{auction.name}</p>
                  <p className="text-sm font-normal text-slate-500">By {auction.auctionHouse?.name}</p>
                </div>
              </div>
            </Link>
            <Viewer content={auction.description} />
          </div>
          {/* Mobile */}
          <Accordion type="multiple" className="flex w-full flex-col lg:hidden">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-base font-semibold text-slate-900">Overview</AccordionTrigger>
              <AccordionContent>
                <Viewer content={product?.description || '-'} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-base font-semibold text-slate-900">Estimated Price</AccordionTrigger>
              <AccordionContent className="text-pretty text-base font-normal text-muted-foreground">
                {product.maximumPrice > 0
                  ? `${formatCurrency(product.minimumPrice ?? 0)} - ${formatCurrency(product.maximumPrice ?? 0)}`
                  : formatCurrency(product.minimumPrice ?? 0)}
              </AccordionContent>
            </AccordionItem>
            {auction.propertyType === AuctionPropertyType.NON_PROPERTY && (
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-base font-semibold text-slate-900">
                  Payment & Shipping
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-pretty text-base text-muted-foreground">
                  <div className="flex flex-col">
                    <p className="font-semibold">Payment</p>
                    <Viewer content={auction.paymentInformation} />
                  </div>
                  <div className="flex flex-col">
                    <p className="font-semibold">Shipping</p>
                    <Viewer content={auction.shippingInformation} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {auction.propertyType !== AuctionPropertyType.NON_PROPERTY && (
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-base font-semibold text-slate-900">Postal Address</AccordionTrigger>
                <AccordionContent className="text-pretty text-base font-normal text-muted-foreground">
                  {product?.property?.postalAddress || '-'}
                </AccordionContent>
              </AccordionItem>
            )}
            {auction.auctionDocuments.length > 0 && (
              <AccordionItem value="item-5">
                <AccordionTrigger className="text-base font-semibold text-slate-900">Documents</AccordionTrigger>
                <AccordionContent className="text-pretty text-base font-normal text-muted-foreground">
                  {auction.auctionDocuments.map((document, index) => (
                    <div className="my-2 flex items-center justify-between" key={index}>
                      <div className="flex items-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg border">
                          <File />
                        </div>
                        <div className="ml-4 flex flex-col">
                          <span className="text-sm">{document?.name}</span>
                          <span className="text-xs text-slate-500">{formatFileSize(document?.size)}</span>
                        </div>
                      </div>
                      <div>
                        <Button variant="ghost" className="p-2">
                          <a href={document.src} target="_blank" rel="noreferrer" download>
                            <Download size={16} />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </div>

      <div className="my-10">
        {productList.totalCount > 1 && (
          <div>
            <p className="mb-4 text-xl font-medium">Other lots in this auction</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {productList.list
                .filter((product) => product.id !== currentProductId)
                .slice(0, 8)
                .map((product) => (
                  <OptimizedProductCard
                    key={product.id}
                    product={product as unknown as Awaited<ReturnType<typeof optimizedGetProductList>>['list'][number]}
                  />
                ))}
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={openConfirmBid}
        onOpenChange={(open: boolean) => {
          if (!open) setOpenConfirmBid(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              Please confirm to submit the Max Bid of {formatCurrency(parseFloat(selectedBid))}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOpenConfirmBid(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={'primary'}
              disabled={fetcher.state === 'submitting' || !!product.lockBidding}
              onClick={handleSubmitBid}
            >
              {fetcher.state === 'submitting' && <LoaderCircle className="mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default function WrappedProductDetail() {
  const data = useTypedLoaderData<typeof loader>();
  const { product } = data;

  return (
    // We do this to reset component state on slug change
    <Fragment key={product.id}>
      <ProductDetail />
    </Fragment>
  );
}

const BidIncrementDisplay: React.FC<BidIncrementDisplayProps> = ({ bidIncrements }) => {
  return (
    <div className="space-y-1">
      <h4 className="text-sm font-semibold">Bid Increments</h4>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-xs font-medium">Price</p>
        </div>
        <div>
          <p className="text-xs font-medium">Increments</p>
        </div>
      </div>
      {bidIncrements.map((increment, index) => (
        <div key={index} className="grid grid-cols-2 gap-2">
          <div className="text-xs">
            <p>{`${formatCurrency(increment.priceFrom)} - ${formatCurrency(increment.priceTo)}`}</p>
          </div>
          <div className="text-xs">
            <p>{formatCurrency(increment.increments)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
